import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";
import { ShieldCheck, Umbrella, CheckCircle2, Printer, Bluetooth, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PosLockedOverlay } from "@/components/layout/PosShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { formatBs, formatDate } from "@/lib/format";
import { getPrinterSettings } from "@/lib/printerConfig";
import { printSeguroViaBluetooth, isWebBluetoothAvailable } from "@/lib/bluetoothPrint";
import { printSeguroViaSerial, isWebSerialAvailable } from "@/lib/serialPrint";
import { systemPrint, isDesktopApp, isDesktopRawActive, printSeguroViaDesktop } from "@/lib/desktopPrint";

// Catálogo de productos de la alianza Seguros Pirámide (Venta Masiva).
// Connect NO cotiza ni emite pólizas: solo recauda y genera el serial de activación.
const PRODUCTS = [
  { codprod: "VIDA", label: "Vida", codplan: "001", revplan: "001" },
  { codprod: "AUTO", label: "Asistencia Vial", codplan: "001", revplan: "001" },
  { codprod: "FUNE", label: "Servicio Funerario", codplan: "001", revplan: "001" },
  { codprod: "SALU", label: "Salud", codplan: "001", revplan: "001" },
];

export default function PosSegurosPage() {
  const { user } = useAuth();
  const gate = useOutletContext() || {};
  const locked = !!gate.suspended;
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [product, setProduct] = useState(PRODUCTS[0]);
  const [currency, setCurrency] = useState("BS");
  const [monto, setMonto] = useState("");
  const [comprobante, setComprobante] = useState(null);
  const [paperSize, setPaperSize] = useState(() => getPrinterSettings().paper_size);
  const [btPrinting, setBtPrinting] = useState(false);
  const idemRef = useRef(null);
  const autoPrintedRef = useRef(null);

  const { data: rate } = useQuery({
    queryKey: ["exchange-rate-current"],
    queryFn: async () => (await api.get("/exchange-rates/current")).data,
    refetchInterval: 60000,
  });
  const bcv = Number(rate?.bs_per_usd || 0);

  const montoNum = Number(monto) || 0;
  const { montoBs, montoUsd } = useMemo(() => {
    if (currency === "USD") {
      return { montoBs: bcv > 0 ? montoNum * bcv : 0, montoUsd: montoNum };
    }
    return { montoBs: montoNum, montoUsd: bcv > 0 ? montoNum / bcv : 0 };
  }, [montoNum, currency, bcv]);

  const emitirMut = useMutation({
    mutationFn: async () => {
      if (!idemRef.current) idemRef.current = crypto.randomUUID();
      return (await api.post("/seguros/venta-masiva/emitir", {
        customer_name: customerName.trim(),
        customer_id: customerId.trim(),
        customer_phone: customerPhone.trim() || undefined,
        codprod: product.codprod,
        codplan: product.codplan,
        revplan: product.revplan,
        monto: montoNum,
        currency,
        idempotency_key: idemRef.current,
      })).data;
    },
    onSuccess: (d) => {
      setComprobante({ ...d, product_label: product.label });
      toast.success("Seguro cobrado · serial generado");
    },
    onError: (e) => {
      idemRef.current = null;
      toast.error(e?.response?.data?.detail || "No se pudo procesar la venta");
    },
  });

  const canEmit =
    !locked &&
    customerId.trim().length > 3 &&
    customerName.trim().length > 2 &&
    !!product &&
    montoNum > 0 &&
    !emitirMut.isPending;

  const resetForm = () => {
    setCustomerId("");
    setCustomerName("");
    setCustomerPhone("");
    setProduct(PRODUCTS[0]);
    setMonto("");
    setCurrency("BS");
    setComprobante(null);
    idemRef.current = null;
    autoPrintedRef.current = null;
  };

  const printBluetooth = async () => {
    if (!comprobante) return;
    setBtPrinting(true);
    const s = getPrinterSettings();
    const res = await printSeguroViaBluetooth(comprobante, {
      operatorName: user?.full_name || "",
      paperSize,
      feedLines: s.feed_lines,
      cutPaper: s.cut_paper,
    });
    setBtPrinting(false);
    if (res.ok) toast.success("Comprobante enviado a la impresora Bluetooth");
    else toast.error(res.error || "No se pudo imprimir por Bluetooth");
  };
  const printSerial = async () => {
    if (!comprobante) return;
    const s = getPrinterSettings();
    const ctx = { operatorName: user?.full_name || "", paperSize, feedLines: s.feed_lines, cutPaper: s.cut_paper, baudRate: s.serial_baud };
    const res = isDesktopRawActive() ? await printSeguroViaDesktop(comprobante, ctx) : await printSeguroViaSerial(comprobante, ctx);
    if (res.ok) toast.success("Comprobante enviado a la impresora directa");
    else toast.error(res.error || "No se pudo imprimir en la impresora directa");
  };
  // Botón Imprimir: ESC/POS directo si el método es serie; si no, navegador.
  const printManual = () => (isDesktopRawActive() || (getPrinterSettings().print_method === "serial" && isWebSerialAvailable()) ? printSerial() : systemPrint());

  useEffect(() => {
    if (!comprobante || comprobante.status !== "approved") return;
    if (autoPrintedRef.current === comprobante.id) return;
    autoPrintedRef.current = comprobante.id;
    const s = getPrinterSettings();
    if (!s.auto_print_on_sell) return;
    if (s.print_method === "bluetooth" && isWebBluetoothAvailable()) printBluetooth();
    else if (isDesktopRawActive() || (s.print_method === "serial" && isWebSerialAvailable())) printSerial();
    else setTimeout(() => systemPrint(), 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comprobante]);

  return (
    <div className="relative px-4 sm:px-6 py-5 max-w-3xl mx-auto space-y-5" data-testid="pos-seguros-page">
      <PosLockedOverlay />
      <fieldset disabled={locked} aria-disabled={locked} className="space-y-5 min-w-0" data-testid="pos-seguros-fieldset">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/15 grid place-items-center">
          <Umbrella className="text-primary" size={20} />
        </div>
        <div>
          <h1 className="text-xl font-bold leading-tight">Seguros Pirámide</h1>
          <p className="text-sm text-muted-foreground">Venta Masiva · Recaudación con serial de activación</p>
        </div>
        {bcv > 0 && (
          <Badge variant="outline" className="ml-auto gap-1.5 border-primary/40 text-primary" data-testid="seguros-bcv-rate">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">BCV</span>
            <span className="font-mono tabular-nums">{formatBs(bcv)}</span>
          </Badge>
        )}
      </div>

      {!comprobante && (
        <Card className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label>Producto de la alianza</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PRODUCTS.map((pr) => (
                <button
                  key={pr.codprod}
                  type="button"
                  onClick={() => setProduct(pr)}
                  className={`rounded-lg border px-3 py-3 text-sm font-medium transition-colors text-center ${
                    product?.codprod === pr.codprod
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-foreground hover:bg-muted"
                  }`}
                  data-testid={`seguros-product-${pr.codprod.toLowerCase()}`}
                >
                  {pr.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Cédula / RIF del cliente</Label>
              <Input
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                placeholder="V-12345678"
                data-testid="seguros-cedula-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nombre completo</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Nombre y apellido"
                data-testid="seguros-name-input"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Teléfono (opcional)</Label>
              <Input
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="0414..."
                data-testid="seguros-phone-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Monto a cobrar</Label>
            <div className="flex gap-2">
              <div className="inline-flex rounded-md border overflow-hidden shrink-0">
                {["BS", "USD"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCurrency(c)}
                    className={`px-3 text-sm font-medium transition-colors ${
                      currency === c ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground"
                    }`}
                    data-testid={`seguros-currency-${c.toLowerCase()}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <Input
                type="number"
                inputMode="decimal"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="0,00"
                data-testid="seguros-monto-input"
              />
            </div>
            {montoNum > 0 && bcv > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="seguros-monto-conversion">
                {currency === "USD"
                  ? `≈ Bs ${formatBs(montoBs)}`
                  : `≈ $ ${montoUsd.toFixed(2)}`}{" "}
                · Tasa BCV {formatBs(bcv)}
              </p>
            )}
          </div>

          <Button
            className="w-full h-14 text-base"
            onClick={() => emitirMut.mutate()}
            disabled={!canEmit}
            data-testid="seguros-submit-button"
          >
            {emitirMut.isPending ? <Loader2 className="animate-spin mr-2" size={18} /> : <ShieldCheck className="mr-2" size={18} />}
            Cobrar y generar serial {montoNum > 0 ? `· Bs ${formatBs(montoBs)}` : ""}
          </Button>
        </Card>
      )}

      {comprobante && (
        <Card className="p-4 border-emerald-500/40" data-testid="seguros-comprobante">
          <div className="flex items-center gap-2 mb-3 no-print">
            <CheckCircle2 className="text-emerald-500" size={22} />
            <div className="font-bold text-lg">Seguro cobrado</div>
            <Badge variant="outline" className="ml-auto uppercase">{comprobante.status}</Badge>
          </div>

          <div className="flex items-center gap-2 mb-3 no-print">
            <span className="text-xs text-muted-foreground">Papel:</span>
            <div className="inline-flex rounded-md border overflow-hidden">
              {["58mm", "80mm"].map((sz) => (
                <button
                  key={sz}
                  onClick={() => setPaperSize(sz)}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    paperSize === sz ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground"
                  }`}
                  data-testid={`seguros-paper-${sz.replace("mm", "")}`}
                >
                  {sz}
                </button>
              ))}
            </div>
          </div>

          <div
            id="seguros-print-area"
            className={`mx-auto bg-white text-black font-mono text-[12px] leading-tight p-3 rounded border ${
              paperSize === "80mm" ? "thermal-80 max-w-[300px]" : "thermal-58 max-w-[220px]"
            }`}
            data-testid="seguros-thermal-receipt"
          >
            <div className="text-center">
              <div className="font-bold text-base tracking-wide">CONNECT</div>
              <div className="text-[11px]">SISTEMA DE JUEGOS</div>
              <div className="font-bold text-[11px] mt-0.5">SEGUROS PIRAMIDE</div>
              <div className="text-[10px]">Venta Masiva</div>
              <div className="text-[10px]">{user?.full_name || ""}</div>
              <div className="text-[10px]">{formatDate(comprobante.created_at)}</div>
            </div>
            <div className="border-t border-dashed border-black my-1.5" />
            <TRow k="Producto" v={comprobante.product_label || comprobante?.request_payload?.codprod || "—"} bold testid="seguros-product-value" />
            <TRow k="Cedula/RIF" v={comprobante.customer_id || "—"} />
            <TRow k="Cliente" v={comprobante.customer_name || "—"} />
            <div className="border-t border-dashed border-black my-1.5" />
            <div className="flex justify-between font-bold text-[15px]">
              <span>MONTO Bs</span>
              <span>{formatBs(comprobante.monto_bs)}</span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span>Total USD</span>
              <span>$ {Number(comprobante.monto_usd || 0).toFixed(2)}</span>
            </div>
            <div className="border-t border-dashed border-black my-1.5" />
            <div className="text-center my-1">
              <div className="text-[10px]">SERIAL DE ACTIVACION</div>
              <div className="font-bold text-[13px] break-all" data-testid="seguros-serial">{comprobante.policy_number || "—"}</div>
            </div>
            <div className="border-t border-dashed border-black my-1.5" />
            {comprobante.activation_url && (
              <div className="flex flex-col items-center gap-1 my-2">
                <div className="text-[10px] font-bold">ACTIVE SU SEGURO</div>
                <div className="bg-white p-1" data-testid="seguros-qr">
                  <QRCodeSVG value={comprobante.activation_url} size={paperSize === "80mm" ? 140 : 110} level="M" />
                </div>
                <div className="text-[8px] break-all text-center px-1">
                  {comprobante.activation_url.replace(/^https?:\/\//, "")}
                </div>
              </div>
            )}
            <div className="border-t border-dashed border-black my-1.5" />
            <div className="text-center text-[10px] mt-1">
              <div>Escanee el QR para activar</div>
              <div>Validez sujeta a activacion en Piramide</div>
              <div>Conserve este comprobante</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4 no-print">
            <Button variant="outline" onClick={printManual} data-testid="seguros-print-button">
              <Printer size={16} className="mr-2" /> Imprimir ({isDesktopRawActive() || (getPrinterSettings().print_method === "serial" && isWebSerialAvailable()) ? "directa" : isDesktopApp() ? "sistema" : "navegador"})
            </Button>
            <Button
              variant="outline"
              onClick={printBluetooth}
              disabled={btPrinting || !isWebBluetoothAvailable()}
              data-testid="seguros-print-bluetooth-button"
            >
              {btPrinting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Bluetooth size={16} className="mr-2" />}
              Imprimir Bluetooth
            </Button>
            <Button variant="ghost" onClick={resetForm} data-testid="seguros-new-button">
              <RefreshCw size={16} className="mr-2" /> Nueva Venta
            </Button>
          </div>
        </Card>
      )}
      </fieldset>
    </div>
  );
}

function TRow({ k, v, testid, bold }) {
  return (
    <div className="flex justify-between gap-3">
      <span>{k}</span>
      <span className={`text-right ${bold ? "font-bold" : ""}`} data-testid={testid}>{v}</span>
    </div>
  );
}
